import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import '../features/features-help-guide.css';
import './campaigns.css';

const GUIDE_STEPS = [
  {
    key: 'list',
    tone: 'teal',
    titleKey: 'CampaignsHelp.Step1Title',
    titleDefault: 'Running campaigns come first',
    bodyKey: 'CampaignsHelp.Step1Body',
    bodyDefault:
      'Open View campaigns to see the grid. Sending, scheduled, and paused rows stay at the top. Use filters to find a location, channel, or status.',
  },
  {
    key: 'create',
    tone: 'orange',
    titleKey: 'CampaignsHelp.Step2Title',
    titleDefault: 'Create from a Studio template',
    bodyKey: 'CampaignsHelp.Step2Body',
    bodyDefault:
      'New campaign opens a dialog. Choose a location, SMS or email, an audience (one patient, everyone at the location, or a zip code), and a Studio template for that location. Drafts appear in the list. Publish the template before you send. Preview count and price first.',
  },
  {
    key: 'send',
    tone: 'purple',
    titleKey: 'CampaignsHelp.Step3Title',
    titleDefault: 'Save a draft or send',
    bodyKey: 'CampaignsHelp.Step3Body',
    bodyDefault:
      'Save draft keeps the campaign in the list without sending. Create campaign delivers now, or schedules for 8:00 AM if it is overnight. Promotional messages never go out between 8:00 PM and 8:00 AM.',
  },
  {
    key: 'actions',
    tone: 'green',
    titleKey: 'CampaignsHelp.Step4Title',
    titleDefault: 'Edit, pause, resume, cancel, or delete',
    bodyKey: 'CampaignsHelp.Step4Body',
    bodyDefault:
      'Each row shows edit, send, pause, resume, cancel, delete, and audit history. Pause holds a campaign so remaining messages do not go out. Resume continues a paused campaign. Cancel stops remaining messages and can also close a draft, sent, or failed campaign. A cancelled campaign cannot be sent again — delete it, or create a new one. Sending campaigns cannot be edited or deleted until they finish, pause, or cancel. Use Refresh to reload the list.',
  },
  {
    key: 'analytics',
    tone: 'coral',
    titleKey: 'CampaignsHelp.Step5Title',
    titleDefault: 'Analytics and billing',
    bodyKey: 'CampaignsHelp.Step5Body',
    bodyDefault:
      'The Analytics page under Campaigns shows delivery outcomes, messages sent vs skipped, and the amount charged. Pricing is per delivered SMS or email, not a flat campaign fee. Use the history table for errors and billed amount on each send.',
  },
  {
    key: 'audit',
    tone: 'teal',
    titleKey: 'CampaignsHelp.Step6Title',
    titleDefault: 'Who created, changed, paused, or sent a campaign',
    bodyKey: 'CampaignsHelp.Step6Body',
    bodyDefault:
      'The list shows Created by, Modified by, and Last action. Open Audit history on a row for create, edit, send, schedule, pause, resume, cancel, and delete. The same entries appear on the Audit Trail report under Campaigns.',
  },
];

const RULE_COPY = [
  {
    id: 'separate_path',
    titleKey: 'CampaignsHelp.RuleSeparateTitle',
    titleDefault: 'Promotional, not transactional',
    detailKey: 'CampaignsHelp.RuleSeparateDetail',
    detailDefault:
      'Campaigns use their own send path. Ready, reminder, and pickup messages stay on Studio.',
  },
  {
    id: 'quiet_hours',
    titleKey: 'CampaignsHelp.RuleQuietTitle',
    titleDefault: 'No overnight sends',
    detailKey: 'CampaignsHelp.RuleQuietDetail',
    detailDefault:
      'Promotional SMS and email cannot go out from 8:00 PM to 8:00 AM in the location time zone. Sends in that window wait until 8:00 AM.',
  },
  {
    id: 'stop',
    titleKey: 'CampaignsHelp.RuleStopTitle',
    titleDefault: 'STOP and HELP',
    detailKey: 'CampaignsHelp.RuleStopDetail',
    detailDefault:
      'Every promotional SMS must include STOP to opt out and HELP. Twilio and CTIA A2P 10DLC promotional traffic require both.',
  },
  {
    id: 'consent',
    titleKey: 'CampaignsHelp.RuleConsentTitle',
    titleDefault: 'Consent and opt-out',
    detailKey: 'CampaignsHelp.RuleConsentDetail',
    detailDefault:
      'SMS goes only to patients with a phone on file who have not opted out. A STOP reply is honored for later campaigns.',
  },
  {
    id: 'identify',
    titleKey: 'CampaignsHelp.RuleIdentifyTitle',
    titleDefault: 'Identify the pharmacy',
    detailKey: 'CampaignsHelp.RuleIdentifyDetail',
    detailDefault:
      'The message must name the pharmacy. Campaigns merge Account Name from the Studio template.',
  },
  {
    id: 'length',
    titleKey: 'CampaignsHelp.RuleLengthTitle',
    titleDefault: 'Length',
    detailKey: 'CampaignsHelp.RuleLengthDetail',
    detailDefault:
      'SMS stays within 3 segments, the same cap as Studio.',
  },
  {
    id: 'pricing',
    titleKey: 'CampaignsHelp.RulePricingTitle',
    titleDefault: 'Priced per message',
    detailKey: 'CampaignsHelp.RulePricingDetail',
    detailDefault:
      'Each delivered SMS or email is billed per message. Preview shows the estimate before send.',
  },
];

const displayRules = (rules, t) =>
  RULE_COPY.map((copy) => {
    const fromApi = (rules || []).find((rule) => rule.id === copy.id);
    return {
      id: copy.id,
      title: t(copy.titleKey, {
        defaultValue: (fromApi && fromApi.title) || copy.titleDefault,
      }),
      detail: t(copy.detailKey, {
        defaultValue: (fromApi && fromApi.detail) || copy.detailDefault,
      }),
    };
  }).concat(
    (rules || []).filter(
      (rule) => !RULE_COPY.some((copy) => copy.id === rule.id)
    )
  );

export const CampaignsHelpGuide = ({
  t,
  open,
  tab,
  onTabChange,
  onClose,
  rules,
}) => {
  const currentTab = tab === 'rules' ? 'rules' : 'guide';
  const items = displayRules(rules, t);
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      className="features-help-dialog"
      style={{ zIndex: 1500 }}
      PaperProps={{ className: 'features-help-paper' }}
    >
      <div className="features-help-hero">
        <IconButton
          aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
          onClick={onClose}
          className="features-help-close"
        >
          <CloseIcon />
        </IconButton>
        <p className="features-help-eyebrow">
          {t('CampaignsHelp.Eyebrow', { defaultValue: 'Campaigns' })}
        </p>
        <h2>
          {t('CampaignsHelp.Title', {
            defaultValue: 'Campaigns guide',
          })}
        </h2>
        <p>
          {t('CampaignsHelp.Subtitle', {
            defaultValue:
              'How to send promotional SMS or email, and the Twilio rules that apply to every campaign.',
          })}
        </p>
      </div>
      <Tabs
        value={currentTab}
        onChange={(event, next) => {
          if (typeof onTabChange === 'function') onTabChange(next);
        }}
        className="campaigns-help-tabs"
        textColor="inherit"
        indicatorColor="primary"
      >
        <Tab
          value="guide"
          label={t('CampaignsHelp.GuideTab', { defaultValue: 'Guide' })}
        />
        <Tab
          value="rules"
          label={t('CampaignsHelp.RulesTab', {
            defaultValue: 'Promotional rules',
          })}
        />
      </Tabs>
      <DialogContent className="features-help-body">
        {currentTab === 'guide' ? (
          <React.Fragment>
            {GUIDE_STEPS.map((step, index) => (
              <section
                key={step.key}
                className={'features-help-step campaigns-help-step is-' + step.tone}
              >
                <div className="features-help-step-copy">
                  <span className="features-help-step-num">{index + 1}</span>
                  <h3>{t(step.titleKey, { defaultValue: step.titleDefault })}</h3>
                  <p>{t(step.bodyKey, { defaultValue: step.bodyDefault })}</p>
                </div>
              </section>
            ))}
            <div className="features-help-tips">
              <h3>
                {t('CampaignsHelp.TipsTitle', { defaultValue: 'Good to know' })}
              </h3>
              <ul>
                <li>
                  {t('CampaignsHelp.Tip1', {
                    defaultValue:
                      'Publish the Studio template first. Campaigns only send from published copy.',
                  })}
                </li>
                <li>
                  {t('CampaignsHelp.Tip2', {
                    defaultValue:
                      'Campaigns is a separate product from Studio. Ready and pickup messages are not sent from this page.',
                  })}
                </li>
                <li>
                  {t('CampaignsHelp.Tip3', {
                    defaultValue:
                      'Open the Promotional rules tab for STOP, HELP, opt-out, overnight hours, and per-message pricing.',
                  })}
                </li>
              </ul>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p className="campaigns-help-rules-intro">
              {t('CampaignsHelp.RulesIntro', {
                defaultValue:
                  'Twilio and CTIA A2P promotional SMS rules. These apply to every campaign send, including scheduled overnight sends.',
              })}
            </p>
            {items.map((rule) => (
              <article key={rule.id} className="campaigns-help-rule">
                <h3>{rule.title}</h3>
                <p>{rule.detail}</p>
              </article>
            ))}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions className="features-help-actions">
        {currentTab === 'guide' ? (
          <Button
            onClick={() => {
              if (typeof onTabChange === 'function') onTabChange('rules');
            }}
            sx={{ textTransform: 'none', marginRight: 'auto' }}
          >
            {t('CampaignsHelp.OpenRules', {
              defaultValue: 'View promotional rules',
            })}
          </Button>
        ) : null}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('MessageControl.Close', { defaultValue: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CampaignsHelpButton = ({ t, size = 'small', onOpen }) => (
  <Button
    variant="outlined"
    size={size}
    startIcon={<HelpOutlineIcon />}
    onClick={() => {
      if (typeof onOpen === 'function') onOpen('guide');
    }}
    sx={{
      textTransform: 'none',
      fontWeight: 600,
      borderColor: '#7dd3fc',
      color: '#0369a1',
      background: '#fff',
      '&:hover': {
        borderColor: '#00a7e1',
        background: 'rgba(0, 167, 225, 0.06)',
      },
    }}
  >
    {t('CampaignsHelp.OpenGuide', { defaultValue: 'Help guide' })}
  </Button>
);

export { GUIDE_STEPS, RULE_COPY, displayRules };

export default CampaignsHelpGuide;



// WEBPACK FOOTER //
// ./src/components/campaigns/CampaignsHelpGuide.js